'use client'

import { useId, useState, type ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import { LibraryBig, Shuffle } from 'lucide-react'

import type { FeaturedMoodTile, MoodFilter, NonFeaturedMood } from '@/lib/blog/moods'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel'

import { ALL_STORIES_COLLECTION, openCollection, openMood } from './mood-filter'

interface WanderByMoodProps {
    /** The featured moods, in selection order — resolved by `getMoodSectionData`. */
    featured: FeaturedMoodTile[]
    /** The non-featured registry remainder — surprise-me pool + carousel overflow. */
    nonFeatured: NonFeaturedMood[]
}

/**
 * The mood-browsing section on the blog home: one tile per featured
 * mood — the mood's label over its most-recent post's hero image — plus a
 * "surprise me" control that *reveals* a random NON-featured mood for the reader
 * to click; repeat clicks re-roll to a different one.
 *
 * The remaining hero-bearing moods follow the featured ones in the same
 * carousel as the same tiles (a non-featured mood without a tile image stays
 * surprise-me-only), ending in an "All stories" tile that opens the
 * all-stories view.
 *
 * Opening a mood or the all-stories view does not navigate: {@link openMood} /
 * {@link openCollection} set a query param (via `history.pushState`, so Back
 * returns here) and `BlogHomeContent` swaps the whole blog home to that view.
 * Every tile and the reveal are `<button>`s, not
 * links, so no crawlable URL to any view state exists. Empty/heroless featured
 * moods were omitted upstream; a fully empty section renders nothing.
 */
export const WanderByMood = ({ featured, nonFeatured }: WanderByMoodProps) => {
    const t = useTranslations('blogHome.wanderByMood')
    const headingId = useId()
    const [surprise, setSurprise] = useState<MoodFilter | null>(null)

    if (featured.length === 0) {
        return null
    }

    const overflowTiles = nonFeatured.filter(
        (mood): mood is NonFeaturedMood & { post: NonNullable<NonFeaturedMood['post']> } =>
            mood.post !== undefined,
    )

    const reroll = () => {
        // Draw from the pool minus the current reveal so repeat clicks always
        // move (a same-pick re-roll reads as a dead button).
        const pool = nonFeatured.filter((mood) => mood.slug !== surprise?.slug)
        const pick = pool[Math.floor(Math.random() * pool.length)] ?? surprise
        setSurprise(pick ?? null)
    }

    return (
        <section
            aria-labelledby={headingId}
            className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-10 md:py-14"
        >
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2
                        id={headingId}
                        className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]"
                    >
                        {t('heading')}
                    </h2>
                    <p className="mt-1 text-[15px] text-text-secondary md:text-base">
                        {t('subheading')}
                    </p>
                </div>
                {nonFeatured.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3">
                        {surprise && (
                            <span data-testid="mood-surprise" className="text-[15px] md:text-base">
                                {t('reveal')}{' '}
                                <button
                                    type="button"
                                    onClick={() => openMood(surprise.slug)}
                                    className="font-bold text-text-primary underline underline-offset-4"
                                >
                                    {surprise.label}
                                </button>
                            </span>
                        )}
                        <Button variant="outline" onClick={reroll}>
                            <Shuffle aria-hidden="true" className="size-4" />
                            {t('surpriseMe')}
                        </Button>
                    </div>
                )}
            </div>

            {/* One track for every mood tile — the featured selection simply leads
                it; a separate static grid above the carousel would read as two rows
                of the same thing, one inexplicably scrollable. The all-stories tile
                closes the track and is always rendered, so it stays reachable
                even with no overflow moods. */}
            <Carousel
                aria-label={t('carouselLabel')}
                opts={{ align: 'start' }}
                data-testid="mood-carousel"
            >
                <CarouselContent className="-ml-3 py-2 md:-ml-5">
                    {featured.map((tile) => (
                        <CarouselItem
                            key={tile.slug}
                            className="basis-1/2 pl-3 md:basis-1/4 md:pl-5"
                        >
                            <MoodTileButton
                                label={tile.label}
                                onClick={() => openMood(tile.slug)}
                                image={tile.post.heroImage}
                            />
                        </CarouselItem>
                    ))}
                    {overflowTiles.map((tile) => (
                        <CarouselItem
                            key={tile.slug}
                            className="basis-1/2 pl-3 md:basis-1/4 md:pl-5"
                        >
                            <MoodTileButton
                                label={tile.label}
                                onClick={() => openMood(tile.slug)}
                                image={tile.post.heroImage}
                                testId="mood-carousel-tile"
                            />
                        </CarouselItem>
                    ))}
                    <CarouselItem className="basis-1/2 pl-3 md:basis-1/4 md:pl-5">
                        <MoodTileButton
                            label={t('allStoriesTile')}
                            onClick={() => openCollection(ALL_STORIES_COLLECTION)}
                            testId="mood-carousel-tile"
                        >
                            <span
                                aria-hidden="true"
                                className="absolute inset-0 flex items-center justify-center bg-primary/10"
                            >
                                <LibraryBig className="size-8 text-primary" />
                            </span>
                        </MoodTileButton>
                    </CarouselItem>
                </CarouselContent>
                <CarouselPrevious className="left-2" />
                <CarouselNext className="right-2" />
            </Carousel>
        </section>
    )
}

interface MoodTileButtonProps {
    label: string
    onClick: () => void
    image?: { url: string; alt: string } | undefined
    /** Custom tile face (the all-stories tile) — rendered where the image would be. */
    children?: ReactNode
    /** Featured tiles carry the `mood-tile` id; carousel tiles get their own. */
    testId?: string
}

/**
 * One mood tile: the label over its image, as a `<button>` — never an anchor,
 * so the mood/collection views stay uncrawlable.
 */
const MoodTileButton = ({
    label,
    onClick,
    image,
    children,
    testId = 'mood-tile',
}: MoodTileButtonProps) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={cn(
            'group relative block w-full overflow-hidden rounded-xl bg-muted text-left shadow-sm',
            'transition-shadow duration-300 hover:shadow-lg',
            'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
            'h-[130px] md:h-[190px]',
        )}
    >
        {image && (
            <Image
                src={image.url}
                alt={image.alt}
                fill
                unoptimized
                sizes="(min-width: 768px) 25vw, 50vw"
                className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.05]"
            />
        )}
        {children}
        <span
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-foreground/80 to-transparent"
        />
        <span className="absolute bottom-3 left-3 text-[15px] font-bold text-background md:text-lg">
            {label}
        </span>
    </button>
)
